import { ChurnRiskFactors, ChurnRiskScore } from '@/lib/churn-risk';

export interface RetentionAction {
  triggered_by: keyof ChurnRiskFactors;
  risk_level: ChurnRiskScore['level'];
  action_text: string;
  action_type: 'call' | 'sms' | 'email' | 'in_person' | 'in_app';
  priority: 'urgent' | 'high' | 'medium' | 'low';
}

interface ActionTemplate {
  factor: keyof ChurnRiskFactors;
  levels: Partial<Record<ChurnRiskScore['level'], {
    actions: Array<{
      text: string | ((clientName: string) => string);
      type: RetentionAction['action_type'];
      priority: RetentionAction['priority'];
    }>;
  }>>;
}

const ACTION_TEMPLATES: ActionTemplate[] = [
  {
    factor: 'missedSessionsIncreasing',
    levels: {
      critical: {
        actions: [
          {
            text: (name) => `Call ${name} to check in — they've been cancelling a lot of sessions lately`,
            type: 'call',
            priority: 'urgent',
          },
        ],
      },
      high: {
        actions: [
          {
            text: (name) => `Send ${name} a message asking if their schedule has changed`,
            type: 'sms',
            priority: 'high',
          },
        ],
      },
    },
  },
  {
    factor: 'sessionFrequencyDecline',
    levels: {
      critical: {
        actions: [
          {
            text: (name) => `Schedule a progress review with ${name} — their training frequency has dropped significantly`,
            type: 'in_app',
            priority: 'urgent',
          },
        ],
      },
      high: {
        actions: [
          {
            text: (name) => `Send ${name} a motivational check-in with their recent progress highlights`,
            type: 'sms',
            priority: 'high',
          },
        ],
      },
    },
  },
  {
    factor: 'noRenewalScheduled',
    levels: {
      critical: {
        actions: [
          {
            text: (name) => `${name}'s package is ending soon with no renewal — have the conversation at their next session`,
            type: 'in_person',
            priority: 'urgent',
          },
        ],
      },
      high: {
        actions: [
          {
            text: (name) => `Send ${name} a renewal reminder with a summary of their achievements this package`,
            type: 'email',
            priority: 'high',
          },
        ],
      },
    },
  },
  {
    factor: 'packageEndingSoon',
    levels: {
      high: {
        actions: [
          {
            text: (name) => `Prepare a package renewal proposal for ${name} — include their progress summary`,
            type: 'in_app',
            priority: 'medium',
          },
        ],
      },
    },
  },
  {
    factor: 'longGapSinceLastSession',
    levels: {
      critical: {
        actions: [
          {
            text: (name) => `It's been a while since ${name}'s last session — reach out today`,
            type: 'call',
            priority: 'urgent',
          },
        ],
      },
      high: {
        actions: [
          {
            text: (name) => `Send ${name} a 'thinking of you' message with a link to book their next session`,
            type: 'sms',
            priority: 'high',
          },
        ],
      },
    },
  },
  {
    factor: 'resultsStalling',
    levels: {
      critical: {
        actions: [
          {
            text: (name) => `Schedule a program review for ${name} — they've been plateauing and need a fresh approach`,
            type: 'in_person',
            priority: 'urgent',
          },
        ],
      },
      high: {
        actions: [
          {
            text: (name) => `Update ${name}'s program with new exercises or rep schemes to break through their plateau`,
            type: 'in_app',
            priority: 'high',
          },
        ],
      },
    },
  },
  {
    factor: 'streakBroken',
    levels: {
      medium: {
        actions: [
          {
            text: (name) => `Encourage ${name} — they lost their streak. Help them start fresh`,
            type: 'sms',
            priority: 'medium',
          },
        ],
      },
    },
  },
  {
    factor: 'lowEngagement',
    levels: {
      high: {
        actions: [
          {
            text: (name) => `Set up habit tracking for ${name} to increase their engagement between sessions`,
            type: 'in_app',
            priority: 'medium',
          },
        ],
      },
    },
  },
];

/**
 * Generate retention actions based on a client's churn risk analysis.
 * Returns a list of actions that should be created (before deduplication).
 */
export function generateRetentionActions(
  clientName: string,
  riskScore: ChurnRiskScore,
): RetentionAction[] {
  const actions: RetentionAction[] = [];
  const { factors, level } = riskScore;

  for (const template of ACTION_TEMPLATES) {
    // Only generate actions for factors that are active
    if (!factors[template.factor]) continue;

    // Find the highest applicable level config
    // If the overall risk is critical and the template has a critical config, use it
    // Otherwise fall down to high, medium, etc.
    const levelsToCheck: ChurnRiskScore['level'][] = (() => {
      switch (level) {
        case 'critical': return ['critical', 'high', 'medium'];
        case 'high': return ['high', 'medium'];
        case 'medium': return ['medium'];
        default: return [];
      }
    })();

    for (const checkLevel of levelsToCheck) {
      const levelConfig = template.levels[checkLevel];
      if (levelConfig) {
        for (const actionConfig of levelConfig.actions) {
          const text = typeof actionConfig.text === 'function'
            ? actionConfig.text(clientName)
            : actionConfig.text;

          actions.push({
            triggered_by: template.factor,
            risk_level: level,
            action_text: text,
            action_type: actionConfig.type,
            priority: actionConfig.priority,
          });
        }
        break; // Only use the highest matching level
      }
    }
  }

  return actions;
}

/**
 * Deduplication window in days. Actions for the same factor + client
 * within this window won't be recreated.
 */
export const DEDUP_WINDOW_DAYS = 7;

/**
 * Hours after which a critical pending action triggers an escalation.
 */
export const ESCALATION_THRESHOLD_HOURS = 48;
